/**
 * @typedef {import('./schema').UserProperties} UserProperties
 */
import * as constants from "@redbubble/design-system/react/constants";
import { format, parse, isValid } from "date-fns";
import round from "lodash/round";
import GaUserTypes from "../analytics/gaUserTypes";
import { serializeExperiments } from "../experiment";
import {
  AnalyticsContextTypes,
  AnalyticsLocalStorageKeys,
  AnalyticsItemsListIDNameMap,
  AnalyticsItemsList,
} from "./constants";
import config from "../../../config";
import { clientError } from "../errorReporting";
import { storage } from "../persistentStorage";
import { RbGtmClient } from "../../lib/gtm/client";

/**
 *
 * @param {string} date
 * @returns {string}
 */
export const formatDate = (date) => {
  const parsedDate = parse(date);
  if (date && isValid(parsedDate)) {
    return format(parsedDate, "YYYYMMDD");
  }
  return date;
};

// Cannot use dot notation, would need to allow dangling underscore
// eslint-disable-next-line dot-notation
const gaId = (cookies) => (cookies["_ga"] ? cookies["_ga"] : "");

/**
 * @typedef {Object} buildUserPropertiesArgs
 * @property {Object} userInfo
 * @property {Object} userAccount
 * @property {Object} cookies
 * @property {String} rbVisitorID
 * @returns {UserProperties}
 */
export const buildUserProperties = ({
  userInfo,
  userAccount,
  cookies,
  rbVisitorID,
}) => {
  return {
    event: "user_properties",
    custom_client_id: gaId(cookies), // should we do an eary return if there is no GA client id? this will be missing for initial page_view event
    experiment_enrolments: userInfo
      ? serializeExperiments(userInfo.enrolments)
      : "none",
    federated_id: userInfo ? userInfo.federatedId : "",
    joined_date:
      userAccount && userAccount.joinedAt
        ? formatDate(userAccount.joinedAt)
        : "",
    locale: userInfo ? userInfo.locale : null,
    onboarded_date:
      userAccount && userAccount.onboardedAt
        ? formatDate(userAccount.onboardedAt)
        : "",
    user_id: userInfo.id,
    user_type: userInfo ? GaUserTypes.getVisitorType(cookies, userInfo) : null,
    visitor_id_cookie: rbVisitorID,
    custom_country_code: userInfo.country,
  };
};

export const getItemValue = (inventoryItem) => {
  const hasPrice = inventoryItem.price && inventoryItem.price.amount;
  const hasDiscount =
    inventoryItem.price.discount && inventoryItem.price.discount.amount;

  if (hasPrice && hasDiscount)
    return String(inventoryItem.price.discount.amount);
  if (hasPrice) return String(inventoryItem.price.amount);
  return "";
};

export function getHostName() {
  const { hostname } = new URL(config("rbmBaseWebUrl"));
  return hostname;
}

export const AnalyticsStorage = {
  isValidKey(key) {
    return Object.values(AnalyticsLocalStorageKeys).includes(key);
  },

  getValue(key) {
    if (!this.isValidKey(key)) {
      clientError(
        `Unable to get analytics local storage value, invalid key provided: "${key}"`
      );
      return null;
    }

    try {
      return storage.getItem(key);
    } catch (error) {
      clientError(`Error getting localStorage for key "${key}":`, error);
      return null;
    }
  },

  setValue(key, value) {
    if (!this.isValidKey(key)) {
      clientError(
        `Unable to set analytics local storage value, invalid key provided: "${key}"`
      );
      return;
    }

    try {
      storage.setItem(key, value);
    } catch (error) {
      clientError(
        `Error setting value for key "${key}" in localStorage:`,
        error
      );
    }
  },

  removeItem(key) {
    if (!this.isValidKey(key)) {
      clientError(
        `Unable to remove analytics local storage entry, invalid key provided: "${key}"`
      );
      return;
    }

    try {
      storage.removeItem(key);
    } catch (error) {
      clientError(
        `Error removing entry for key "${key}" in localStorage:`,
        error
      );
    }
  },
};

/**
 * Get search type from search paramaters in URL
 * or local storage for typeahead search types
 * typeahead search types are preferenced and if
 * used localstorage is reset.
 *
 * @param {URLSearchParams} urlSearchParams
 * @param {string | null | undefined | false} sortOrder
 * @returns string
 */
export const getSearchType = (urlSearchParams, sortOrder) => {
  const refTopicType = AnalyticsStorage.getValue(
    AnalyticsLocalStorageKeys.RefContextType
  );
  if (refTopicType) {
    AnalyticsStorage.removeItem(AnalyticsLocalStorageKeys.RefContextType);
    return refTopicType;
  }
  if (sortOrder) {
    return AnalyticsContextTypes.Filter;
  }
  return urlSearchParams.get("ref") !== null
    ? String(urlSearchParams.get("ref"))
    : "";
};

/**
 * Get topic index from localstorage and reset
 * localstorage variable if it exists
 * @returns string
 */
export const getTopicIndex = () => {
  const refTopicIndex = AnalyticsStorage.getValue(
    AnalyticsLocalStorageKeys.RefContextIndex
  );
  if (refTopicIndex) {
    AnalyticsStorage.removeItem(AnalyticsLocalStorageKeys.RefContextIndex);
    return refTopicIndex;
  }
  return "";
};

/**
 * Get topic index from localstorage and reset
 * localstorage variable if it exists
 * @returns string
 */
export const getTopicClickedLabel = () => {
  const refContextLabel = AnalyticsStorage.getValue(
    AnalyticsLocalStorageKeys.RefContextLabel
  );
  if (refContextLabel) {
    AnalyticsStorage.removeItem(AnalyticsLocalStorageKeys.RefContextLabel);
    return refContextLabel;
  }
  return "";
};

export const getSearchFilters = (urlSearchParams) => {
  const keyValuePairs = [];

  urlSearchParams.forEach((v, k) => {
    keyValuePairs.push({ [k]: v });
  });

  return JSON.stringify(keyValuePairs);
};

export const getExternalReferrer = () => {
  try {
    const referrer = new URL(document.referrer);
    return referrer.hostname === getHostName() ? "" : referrer.hostname;
  } catch (_) {
    return "";
  }
};

export const getDiscountValue = (price) => {
  if (price && price.discount && price.discount.percent > 0) {
    return round(price.amount - price.discount.amount, 2);
  }
  return 0;
};

export const parseInventoryItemToAnalyticsItem = (
  inventoryItem,
  index,
  analyticsComponentID
) => {
  if (!inventoryItem) return null;

  return {
    discount: getDiscountValue(inventoryItem.price),
    index: Number.isInteger(index) ? index : null,
    work_id: inventoryItem.workId,
    product_type_id: inventoryItem.productTypeId,
    blank_item_id: inventoryItem.blankItemId,
    inventory_item_id: inventoryItem.id || inventoryItem.inventoryItemId,
    retargeting_id: inventoryItem.marketingProductTypeId,
    item_list_id: analyticsComponentID,
    item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
    item_id: inventoryItem.id || inventoryItem.inventoryItemId,
    price: inventoryItem.price ? inventoryItem.price.amount : null,
    quantity: 1,
    ...(inventoryItem.gaCode && {
      item_category5: inventoryItem.gaCode,
    }),
  };
};

export const parseWorkToAnalyticsItem = (work, index, analyticsComponentID) => {
  if (!work) return null;

  return {
    discount: 0, // does not exist on work object
    index: Number.isInteger(index) ? index : null,
    work_id: work.workId,
    item_id: work.workId,
    item_list_id: analyticsComponentID,
    item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
    price: work.price ? work.price.amount : null, // does not exist on work object
    quantity: 1,
  };
};

export const parseSlideToAnalyticsItem = (
  slide,
  index,
  analyticsComponentID
) => {
  if (!slide) return null;

  return {
    discount: null,
    index: Number.isInteger(index) ? index : null,
    item_category: slide.title,
    item_category2: null,
    item_category3: null,
    item_list_id: analyticsComponentID,
    item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
    item_id: null,
    price: null,
    quantity: 1,
  };
};

export const parseSearchResultToAnalyticsItem = (
  result,
  index,
  analyticsComponentID
) => {
  if (!result) return null;
  return parseInventoryItemToAnalyticsItem(
    result.inventoryItem,
    index,
    analyticsComponentID
  );
};

const parseUserListToAnalyticsItem = (item, index) => {
  if (item.inventoryItemId) {
    return parseInventoryItemToAnalyticsItem(
      item,
      index,
      AnalyticsItemsList.UserList
    );
  }
  return parseWorkToAnalyticsItem(item, index, AnalyticsItemsList.UserList);
};

export function parseFactory(analyticsComponentID) {
  switch (analyticsComponentID) {
    case AnalyticsItemsList.TabbedList:
      return parseSearchResultToAnalyticsItem;
    case AnalyticsItemsList.ProductCarousel:
      return parseSearchResultToAnalyticsItem;
    case AnalyticsItemsList.ProductGrid:
      return parseSearchResultToAnalyticsItem;
    case AnalyticsItemsList.InventoryItemsCarousel:
      return parseSearchResultToAnalyticsItem;
    case AnalyticsItemsList.TagsCarousel:
      return parseSlideToAnalyticsItem;
    case AnalyticsItemsList.Recommendations:
      return parseInventoryItemToAnalyticsItem;
    case AnalyticsItemsList.UserList:
      return parseUserListToAnalyticsItem;
    default:
      return null;
  }
}

export function trackViewItemListEvent(
  items,
  analyticsComponentID,
  topic,
  startIndex = 0
) {
  const parseFunction = parseFactory(analyticsComponentID);

  try {
    const analyticsItems = items.map((item, index) => {
      return parseFunction(item, index + startIndex, analyticsComponentID);
    });

    RbGtmClient.track({
      event: "view_item_list",
      page_location: window.location.href,
      topic: topic || "",
      search_query_id: null,
      ecommerce: {
        item_list_id: analyticsComponentID,
        item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
        items: analyticsItems,
      },
    });
  } catch (error) {
    console.error("There was error reporting view_item_list event", error);
  }
}

export const trackListsPageViewItemList = (
  items,
  index,
  analyticsChunkSize
) => {
  const analyticsItems = items.slice(index, index + analyticsChunkSize);

  if (analyticsItems.length) {
    trackViewItemListEvent(
      analyticsItems,
      AnalyticsItemsList.UserList,
      null,
      index
    );
  }
};

export const onSearchCallback = () => {
  AnalyticsStorage.setValue(
    AnalyticsLocalStorageKeys.RefContextType,
    AnalyticsContextTypes.SearchBox
  );
};

export const onTypaheadResultClick = (type) => {
  switch (type) {
    case constants.TRENDING:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.TrendingTypeahead
      );
      break;
    case constants.RECENT:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.RecentTypeahead
      );
      break;
    case constants.POPULAR:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.PopularTypeahead
      );
      break;
    case constants.ARTISTS:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.ArtistsTypeahead
      );
      break;
    case constants.CATEGORIES:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.CategoriesTypeahead
      );
      break;
    case constants.FALLBACK:
      AnalyticsStorage.setValue(
        AnalyticsLocalStorageKeys.RefContextType,
        AnalyticsContextTypes.FallbackTypeahead
      );
      break;
    default:
      break;
  }
};
